import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { findRecord } from '@warp-drive/utilities/json-api';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import type { Store } from '@warp-drive/core';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import {
  dynamicEligibilityRequest,
  periodsOfEligibilityRequest,
} from 'tio-data/requests/tas-participant-actions';
import type { DynamicEligibility, PeriodOfEligibility } from 'tio-common/types/tuition-assistance';
import { guidedWizardRouteFor } from 'tio-employee/utils/tas/guided-wizard-route';
import { shouldShowLpnSurvey } from 'tio-employee/utils/tas/lpn-survey';
import type SessionContextService from 'tio-employee/services/session-context';

export interface TasManageRouteModel {
  application: TasApplicationModel;
  applicationId: string;
  /** Fetched the same way as the legacy show route — feeds ManagePage's
   * inEligible/canSubmitEvidence gate (see manage-page.gts). */
  dynamicEligibility: boolean;
  programEligibility: string[];
  /**
   * Whether the post-submission modal should offer the LPN discount survey.
   * Resolved only when arriving with `applicationSubmitted` — see the model hook.
   */
  showLpnSurvey: boolean;
}

/** Accepts the URL string and programmatic boolean forms of the post-submission flag. */
export function isArrivingSubmitted(value: unknown): boolean {
  return value === true || value === 'true';
}

const INCLUDES = [
  'tas-courses',
  'tas-courses.tas-assets.attachments',
  'tas-program-instance',
  'tas-program-instance.tas-program-template',
  'tas-program-instance.tas-program-template.conditions.public-assets',
  'tas-program-instance.tas-program-template.tas-field-options',
  'tas-program-instance.tas-program-template.company.company-setting',
  'tas-program-instance.employee.person',
  'tas-program-instance.employee.tas-participant',
  'tas-program-instance.tas-assets.attachments',
  'tas-assets.attachments',
  'tas-assistances',
  'tas-assistances.attachments',
  'tas-assistances.tas-assets',
  'tas-assistances.tas-assets.attachments',
  'tags',
  'comments',
  'pcs-cards.pcs-card-holder',
  'pcs-cards.pcs-company',
  'pcs-cards.pcs-authorizations',
  'pcs-cards.pcs-card-events',
  'pcs-cards.pcs-transactions',
].join(',');

/**
 * Base route for the TAS course-management ("manage") screen, used by the single
 * shared `authenticated.tas.applications.manage` route (see manage-link.ts for how
 * an application is routed here). Every application-detail entry point lands
 * here; the legacy show screen is still reached on purpose by the Classic View
 * comparison link, the History tab, the reimagined "View legacy" action, and
 * direct URLs.
 */
export default class TasManageRoute extends Route {
  @service declare router: RouterService;
  @service declare sessionContext: SessionContextService;
  @service declare store: Store;

  /**
   * `applicationSubmitted` drives the post-submission confirmation modal (the
   * guided wizard lands here with it set instead of routing to `/confirmation`).
   *
   * `replace: true` so clearing the flag on close does not leave a history entry —
   * without it, Back re-opens the modal. Deliberately NO `refreshModel`: the
   * legacy show route sets it for this same param, which makes closing the modal
   * refetch the entire application for nothing.
   */
  queryParams = {
    applicationSubmitted: { replace: true },
  };

  async model({ tas_application_id }: { tas_application_id: string }, transition: Transition) {
    const { content } = (await this.store.request(
      findRecord<TasApplicationModel>('tas-application', tas_application_id, {
        include: INCLUDES,
        reload: true,
      })
    )) as { content: { data: TasApplicationModel } };
    const application = content.data;

    const id = application.tasProgramInstance.employee.tasParticipant.id as string;
    const { code } = application.tasProgramInstance.tasProgramTemplate;

    const [dynamicResponse, periodsResponse] = await Promise.all([
      this.store.request(dynamicEligibilityRequest(id)),
      this.store.request(periodsOfEligibilityRequest(id)),
    ]);
    const dynamicEligibility = (dynamicResponse.content as DynamicEligibility)[code] as boolean;
    const programEligibility = (periodsResponse.content as PeriodOfEligibility)[
      code
    ] as unknown as string[];

    // Read off the transition rather than the model params: the param is declared
    // without `refreshModel`, so it is not a model input — this hook only ever sees
    // it on the arriving transition, which is exactly when the modal opens.
    // Gated because `shouldShowLpnSurvey` costs an institution lookup, and an
    // ordinary visit to this screen must not pay for a survey it will never show.
    const showLpnSurvey = isArrivingSubmitted(transition.to?.queryParams?.['applicationSubmitted'])
      ? await shouldShowLpnSurvey(application, {
          store: this.store,
          sessionContext: this.sessionContext,
        })
      : false;

    return {
      application,
      applicationId: tas_application_id,
      dynamicEligibility,
      programEligibility,
      showLpnSurvey,
    };
  }

  /** Redirects drafts to their wizard, replacing history so Back cannot loop through this redirect. */
  afterModel(model: TasManageRouteModel) {
    const route = guidedWizardRouteFor(
      model.application.typeClassification,
      model.application.state
    );
    if (route) {
      this.router.replaceWith(route, model.applicationId);
    }
  }
}
